/**
 * Shared sidebar IA spec — single source of truth for real AND demo sidebars.
 *
 * Real mode uses it directly: `SIDEBAR_SPEC[user.role]`.
 * Demo mode uses it plus the multi-role merge composer (see `SidebarDemo.jsx`).
 *
 * Route/label/icon maps live here too so both sidebars render identically
 * structured items (only the gating and merge behavior differ).
 */
import {
  LayoutDashboard, Users, Target, FileText, MessageSquare, Calendar,
  CalendarCheck, Phone, Bot, FolderOpen, BarChart3, Settings, CheckSquare,
  UserPlus, ClipboardList, Megaphone, Upload, Mail, FileArchive, Building2,
  PhoneForwarded,
} from "lucide-react";

export const ROUTE = {
  dashboard: "/", contacts: "/customers", leads: "/leads",
  ehiClients: "/ehi-clients",
  comms: "/communications", quotes: "/quotes",
  documents: "/documents",
  demoBookings: "/demo-bookings", calendar: "/calendar",
  dialer: "/dialer", aiAgents: "/ai-agents", resources: "/resources",
  ccmDialer: "/ccm-dialer",
  reports: "/reports", settings: "/settings",
  tasks: "/communications?tab=tasks",
  commsDashboard: "/comms-dashboard",
  templates: "/templates",
};

export const ICON = {
  dashboard: LayoutDashboard, contacts: Users, leads: Target,
  ehiClients: Building2,
  comms: MessageSquare, quotes: FileText,
  documents: FileArchive,
  demoBookings: CalendarCheck, calendar: Calendar,
  dialer: Phone, aiAgents: Bot, resources: FolderOpen,
  ccmDialer: PhoneForwarded,
  reports: BarChart3, settings: Settings, tasks: CheckSquare,
  commsDashboard: BarChart3,
  templates: Mail,
};

export const LABEL = {
  dashboard: "Dashboard", contacts: "Customers", leads: "Leads",
  ehiClients: "EHI Clients",
  comms: "Communications", quotes: "Quotes & Contracts",
  documents: "Documents",
  demoBookings: "Demo Bookings", calendar: "Calendar",
  dialer: "Sales Dialer", aiAgents: "AI Agents",
  ccmDialer: "CCM Dialer",
  resources: "Resources", reports: "Reports", settings: "Settings",
  tasks: "Tasks",
  commsDashboard: "Comms Dashboard",
  templates: "Templates",
};

/* Count badges kept ambient for both real + demo (matches prior UX). */
export const BADGES = { aiAgents: "3", resources: "226" };

/* ── Master sidebar sections ──────────────────────────────────────────────
 *
 * The CRM-mode sidebar is rendered from this union of ALL possible items
 * per section, then filtered by the user's effective capability map (from
 * /api/auth/permissions). A super-admin granting a capability via the
 * Roles & Permissions page therefore causes the corresponding sidebar item
 * to appear immediately on the affected user's next refresh — without
 * needing a code change to add the key into a per-role spec.
 *
 * (Super-user fix May 2026 — "team did CTRL+SHIFT+R and Leads still wasn't
 *  on the left panel" report.)
 *
 * The per-role `SIDEBAR_SPEC` below is retained for two narrow uses:
 *   1. `spec.new`     — which "+ New" dropdown variant to mount.
 *   2. `spec.primary` — which nav row to visually emphasise as the role's
 *                       canonical landing destination (e.g. dialer for
 *                       call_agent).
 * Section keys on the per-role spec are no longer consulted at render-time.
 */
export const MASTER_SIDEBAR_SECTIONS = [
  { label: "OVERVIEW", keys: ["dashboard"] },
  { label: "PIPELINE", keys: ["contacts", "leads", "ehiClients", "comms", "quotes", "documents"] },
  { label: "SCHEDULING & MEETINGS", keys: ["demoBookings", "calendar"] },
  { label: "TOOLS", keys: ["dialer", "ccmDialer", "templates", "tasks", "aiAgents", "resources", "reports"] },
  { label: "SYSTEM", keys: ["settings"] },
];

export const SIDEBAR_SPEC = {
  super_admin: {
    new: "full",
    sections: [
      { label: "OVERVIEW", keys: ["dashboard"] },
      { label: "PIPELINE", keys: ["contacts", "leads", "comms", "quotes"] },
      { label: "SCHEDULING & MEETINGS", keys: ["demoBookings", "calendar"] },
      { label: "TOOLS", keys: ["dialer", "templates", "aiAgents", "resources", "reports"] },
      { label: "SYSTEM", keys: ["settings"] },
    ],
  },
  crm_admin: {
    new: "full",
    sections: [
      { label: "OVERVIEW", keys: ["dashboard"] },
      { label: "PIPELINE", keys: ["contacts", "leads", "comms", "quotes"] },
      { label: "SCHEDULING & MEETINGS", keys: ["demoBookings", "calendar"] },
      { label: "TOOLS", keys: ["dialer", "templates", "aiAgents", "resources", "reports"] },
      { label: "SYSTEM", keys: ["settings"] },
    ],
  },
  sales_manager: {
    new: "sales",
    sections: [
      { label: "OVERVIEW", keys: ["dashboard"] },
      { label: "PIPELINE", keys: ["contacts", "leads", "comms", "quotes"] },
      { label: "SCHEDULING & MEETINGS", keys: ["demoBookings", "calendar"] },
      { label: "TOOLS", keys: ["dialer", "templates", "aiAgents", "resources", "reports"] },
      { label: "SYSTEM", keys: ["settings"] },
    ],
  },
  sales_agent: {
    new: "sales",
    sections: [
      { label: "OVERVIEW", keys: ["dashboard"] },
      { label: "PIPELINE", keys: ["contacts", "leads", "comms", "quotes"] },
      { label: "SCHEDULING & MEETINGS", keys: ["demoBookings", "calendar"] },
      { label: "TOOLS", keys: ["dialer", "templates", "resources"] },
      { label: "SYSTEM", keys: ["settings"] },
    ],
  },
  call_agent: {
    new: null, primary: "dialer",
    sections: [
      { label: "OVERVIEW", keys: ["dashboard"] },
      { label: "MY WORK", keys: ["dialer", "tasks"] },
      { label: "SYSTEM", keys: ["settings"] },
    ],
  },
  demo_agent: {
    new: null, primary: "demoBookings",
    sections: [
      { label: "OVERVIEW", keys: ["dashboard"] },
      { label: "MY WORK", keys: ["demoBookings", "comms", "calendar", "tasks"] },
      { label: "SYSTEM", keys: ["settings"] },
    ],
  },
  bookings_agent: {
    new: null, primary: "demoBookings",
    sections: [
      { label: "OVERVIEW", keys: ["dashboard"] },
      { label: "MY WORK", keys: ["demoBookings", "calendar", "comms", "tasks"] },
      { label: "SYSTEM", keys: ["settings"] },
    ],
  },
  contracts_agent: {
    new: null, primary: "quotes",
    sections: [
      { label: "OVERVIEW", keys: ["dashboard"] },
      { label: "MY WORK", keys: ["quotes", "comms", "tasks"] },
      { label: "SYSTEM", keys: ["settings"] },
    ],
  },
  marketing_manager: {
    new: "marketing",
    sections: [
      { label: "OVERVIEW", keys: ["dashboard"] },
      { label: "PIPELINE", keys: ["contacts", "leads", "comms"] },
      { label: "TOOLS", keys: ["aiAgents", "resources", "reports"] },
      { label: "SYSTEM", keys: ["settings"] },
    ],
  },
  marketing_agent: {
    new: null,
    sections: [
      { label: "OVERVIEW", keys: ["dashboard"] },
      { label: "MY WORK", keys: ["comms", "resources"] },
      { label: "SYSTEM", keys: ["settings"] },
    ],
  },
  read_only: {
    new: null,
    sections: [
      { label: "OVERVIEW", keys: ["dashboard"] },
      { label: "PIPELINE", keys: ["contacts", "leads"] },
      { label: "SYSTEM", keys: ["settings"] },
    ],
  },
};

/* ── "+ New" button dropdown options per variant. Targets are existing
 *    routes — clicking navigates (no new modals / pages). ── */
export const NEW_OPTIONS = {
  full: [
    { key: "lead",     label: "New Lead",      to: "/add-lead",                       Icon: Target,         tint: "#1A73E8" },
    { key: "batch",    label: "Batch Import",  to: "/leads?import=1",                 Icon: Upload,         tint: "#185FA5" },
    { key: "contact",  label: "New Contact",   to: "/contacts?new=1",                 Icon: UserPlus,       tint: "#12B981" },
    { key: "quote",    label: "New Quote",     to: "/quotes?new=1",                   Icon: FileText,       tint: "#1D9E75" },
    { key: "demo",     label: "Schedule Demo", to: "/demo-bookings?schedule=1",       Icon: CalendarCheck,  tint: "#7C3AED" },
    { key: "task",     label: "New Task",      to: "/communications?tab=tasks&new=1", Icon: ClipboardList,  tint: "#D97706" },
    { key: "campaign", label: "New Campaign",  to: "/marketing/campaigns?new=1",      Icon: Megaphone,      tint: "#DC2626" },
  ],
  sales: [
    { key: "lead",    label: "New Lead",      to: "/add-lead",                       Icon: Target,         tint: "#1A73E8" },
    { key: "batch",   label: "Batch Import",  to: "/leads?import=1",                 Icon: Upload,         tint: "#185FA5" },
    { key: "contact", label: "New Contact",   to: "/contacts?new=1",                 Icon: UserPlus,       tint: "#12B981" },
    { key: "quote",   label: "New Quote",     to: "/quotes?new=1",                   Icon: FileText,       tint: "#1D9E75" },
    { key: "demo",    label: "Schedule Demo", to: "/demo-bookings?schedule=1",       Icon: CalendarCheck,  tint: "#7C3AED" },
    { key: "task",    label: "New Task",      to: "/communications?tab=tasks&new=1", Icon: ClipboardList,  tint: "#D97706" },
  ],
  marketing: [
    { key: "contact", label: "New Contact", to: "/contacts?new=1",                   Icon: UserPlus,       tint: "#12B981" },
    { key: "task",    label: "New Task",    to: "/communications?tab=tasks&new=1",   Icon: ClipboardList,  tint: "#D97706" },
  ],
};

/* ── Multi-role merge (demo-only) — defined here so SidebarDemo imports one
 *    module rather than reaching into internals. Documented rules in callers. ── */
const NEW_PRECEDENCE = { full: 3, sales: 2, marketing: 1 };
export function mergeSpecs(roleKeys) {
  const specs = roleKeys.map(k => SIDEBAR_SPEC[k]).filter(Boolean);
  if (specs.length === 0) return SIDEBAR_SPEC.read_only;
  if (specs.length === 1) return specs[0];
  const order = [], byLabel = new Map();
  for (const s of specs) {
    for (const sec of s.sections) {
      if (!byLabel.has(sec.label)) { order.push(sec.label); byLabel.set(sec.label, []); }
      const bucket = byLabel.get(sec.label);
      for (const k of sec.keys) if (!bucket.includes(k)) bucket.push(k);
    }
  }
  const sections = order.map(label => ({ label, keys: byLabel.get(label) }));
  let newVariant = null;
  for (const s of specs) {
    if (!s.new) continue;
    if (!newVariant || (NEW_PRECEDENCE[s.new] || 0) > (NEW_PRECEDENCE[newVariant] || 0)) newVariant = s.new;
  }
  const primaries = new Set(specs.map(s => s.primary).filter(Boolean));
  const primary = primaries.size === 1 ? [...primaries][0] : undefined;
  return { new: newVariant, primary, sections };
}
